import { useSyncExternalStore } from "react"
import { Capacitor } from "@capacitor/core"
import { isCoarsePrimaryPointer } from "@/shared/utils/platform/pointerKind"

/** Breakpoint threshold for fine pointers — matches Tailwind `md` */
const MOBILE_MAX = 768
/** Touch-primary devices (phones, tablets) get the desktop layout only from here (iPad landscape). */
const TOUCH_DESKTOP_MIN = 1024

// Module-level so the snapshot (called per task row) never creates a MediaQueryList.
const MQLS: MediaQueryList[] =
  typeof window === "undefined"
    ? []
    : [
        `(min-width: ${MOBILE_MAX}px)`,
        `(min-width: ${TOUCH_DESKTOP_MIN}px)`,
        "(pointer: coarse)",
        "(orientation: landscape)",
      ].map((q) => window.matchMedia(q))

function subscribe(callback: () => void) {
  for (const mql of MQLS) mql.addEventListener("change", callback)
  return () => {
    for (const mql of MQLS) mql.removeEventListener("change", callback)
  }
}

function getSnapshot() {
  const touchPrimary = Capacitor.isNativePlatform() || isCoarsePrimaryPointer()
  if (!touchPrimary) return window.innerWidth >= MOBILE_MAX
  // Touch-primary: desktop layout only when wide AND landscape (iPad Pro 13" portrait is 1032 px).
  return window.innerWidth >= TOUCH_DESKTOP_MIN && window.innerWidth > window.innerHeight
}

/** Mirrors the layout decision to <html data-layout> so CSS (`desk:` variant) agrees with the JS. */
function syncLayoutAttribute() {
  document.documentElement.dataset.layout = getSnapshot() ? "desktop" : "mobile"
}

if (typeof window !== "undefined") {
  syncLayoutAttribute()
  for (const mql of MQLS) mql.addEventListener("change", syncLayoutAttribute)
}

function getServerSnapshot() {
  return true // SSR fallback — assume desktop
}

/**
 * Reactive breakpoint hook using `useSyncExternalStore`.
 * Fine pointer (mouse): desktop layout from 768px. Touch-primary device
 * (native app or `pointer: coarse`): desktop layout only from 1024px and in
 * landscape, so an iPad in portrait gets the phone layout.
 *
 * @returns `{ isMobile, isDesktop }` — exactly one is true at any time.
 */
export function useBreakpoint() {
  const isDesktop = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  )
  return { isMobile: !isDesktop, isDesktop } as const
}
